import { create } from 'zustand';
import { Order, Product } from '../types';
import { resolveCartLineMoq, snapToMoqMultiple } from '../utils/minOrderQuantity';
import {
  DEFAULT_VAT_INVOICE_TYPE,
  resolveEffectiveVatInvoiceType,
  type VatInvoiceType,
} from '../utils/mallSalePrice';
import cartService, { type ServerCartItem } from '../services/cart.service';
import {
  cartToPersistedLines,
  clearPersistedCart,
  getOrCreateCartImportToken,
  loadPersistedCart,
  persistedLinesToImportPayload,
} from './cartPersistence';

export interface CartItem {
  id?: number;
  product: Product;
  quantity: number;
  effectivePrice: number;
  productVariantId?: number | null;
  variantLabel?: string | null;
  specRemark?: string | null;
  vatInvoiceType?: VatInvoiceType | null;
  addedAt?: number;
  unavailableReason?: string | null;
}

export function sortCartByTimeDesc(cart: CartItem[]): CartItem[] {
  return [...cart].sort((a, b) => (b.addedAt ?? 0) - (a.addedAt ?? 0));
}

export const lineKey = (item: CartItem) =>
  `${item.product.id}_${item.productVariantId != null ? item.productVariantId : '0'}_${item.vatInvoiceType || 'none'}`;

function mapServerCart(items: ServerCartItem[] | undefined): CartItem[] {
  return sortCartByTimeDesc(
    (items || []).map((item) => ({
      id: item.id,
      product: item.product,
      quantity: item.quantity,
      effectivePrice: Number(item.effectivePrice) || 0,
      productVariantId: item.productVariantId ?? null,
      variantLabel: item.variantLabel ?? null,
      specRemark: item.specRemark ?? null,
      vatInvoiceType: resolveEffectiveVatInvoiceType(item.vatInvoiceType, item.product),
      addedAt: item.addedAt,
      unavailableReason: item.unavailableReason ?? null,
    })),
  );
}

function findLine(
  cart: CartItem[],
  productId: number,
  productVariantId?: number | null,
  vatInvoiceType?: VatInvoiceType | null,
) {
  const vid = productVariantId ?? null;
  const inv = vatInvoiceType ?? null;
  return cart.find(
    (item) =>
      item.product.id === productId &&
      (item.productVariantId ?? null) === vid &&
      (item.vatInvoiceType ?? null) === inv,
  );
}

type AddToCartOpts = {
  variantId?: number | null;
  variantLabel?: string | null;
  specRemark?: string | null;
  vatInvoiceType?: VatInvoiceType | null;
};

interface OrderState {
  cart: CartItem[];
  cartLoading: boolean;
  currentOrder: Order | null;

  addToCart: (
    product: Product,
    quantity: number,
    effectivePrice: number,
    opts?: AddToCartOpts,
  ) => Promise<void>;
  bootstrapCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  resetCart: () => void;
  updateCartItem: (
    productId: number,
    quantity: number,
    productVariantId?: number | null,
    vatInvoiceType?: VatInvoiceType | null,
  ) => Promise<void>;
  updateCartItemInvoiceType: (
    productId: number,
    productVariantId: number | null | undefined,
    prevInvoiceType: VatInvoiceType | null | undefined,
    nextInvoiceType: VatInvoiceType,
  ) => Promise<void>;
  removeFromCart: (
    productId: number,
    productVariantId?: number | null,
    vatInvoiceType?: VatInvoiceType | null,
  ) => Promise<void>;
  removeCartItemsByIds: (ids: number[]) => Promise<void>;
  clearCart: () => Promise<void>;
  setCurrentOrder: (order: Order | null) => void;
  getTotalAmount: () => number;
  getCartItemCount: () => number;
}

export const useOrderStore = create<OrderState>((set, get) => ({
  cart: [],
  cartLoading: false,
  currentOrder: null,

  addToCart: async (product, quantity, _effectivePrice, opts) => {
    const variantId = opts?.variantId ?? null;
    const vatInvoiceType =
      resolveEffectiveVatInvoiceType(opts?.vatInvoiceType, product) ?? DEFAULT_VAT_INVOICE_TYPE;
    const lineMoq = resolveCartLineMoq({ product, productVariantId: variantId });
    const response = await cartService.add({
      product_id: product.id,
      quantity: snapToMoqMultiple(quantity, lineMoq),
      product_variant_id: variantId,
      vat_invoice_type: vatInvoiceType,
      spec_remark: opts?.specRemark ?? null,
    });
    set({ cart: mapServerCart(response.items) });
  },

  bootstrapCart: async () => {
    set({ cartLoading: true });
    try {
      const remote = await cartService.list();
      set({ cart: mapServerCart(remote.items) });
      const local = loadPersistedCart();
      if (!local.length) return;
      const token = getOrCreateCartImportToken();
      const imported = await cartService.importLocal({
        import_token: token,
        items: persistedLinesToImportPayload(cartToPersistedLines(local)),
      });
      set({ cart: mapServerCart(imported.items) });
      clearPersistedCart();
    } finally {
      set({ cartLoading: false });
    }
  },

  refreshCart: async () => {
    const remote = await cartService.list();
    set({ cart: mapServerCart(remote.items) });
  },

  resetCart: () => set({ cart: [], currentOrder: null, cartLoading: false }),

  updateCartItem: async (productId, quantity, productVariantId, vatInvoiceType) => {
    const line = findLine(get().cart, productId, productVariantId, vatInvoiceType);
    if (!line?.id) return;
    const response =
      quantity <= 0
        ? await cartService.remove(line.id)
        : await cartService.update(line.id, { quantity });
    set({ cart: mapServerCart(response.items) });
  },

  updateCartItemInvoiceType: async (
    productId,
    productVariantId,
    prevInvoiceType,
    nextInvoiceType,
  ) => {
    const source = findLine(get().cart, productId, productVariantId, prevInvoiceType);
    if (!source?.id) return;
    const response = await cartService.update(source.id, { vat_invoice_type: nextInvoiceType });
    set({ cart: mapServerCart(response.items) });
  },

  removeFromCart: async (productId, productVariantId, vatInvoiceType) => {
    const line = findLine(get().cart, productId, productVariantId, vatInvoiceType);
    if (!line?.id) return;
    const response = await cartService.remove(line.id);
    set({ cart: mapServerCart(response.items) });
  },

  removeCartItemsByIds: async (ids) => {
    const unique = Array.from(new Set(ids.filter((id) => id > 0)));
    if (!unique.length) return;
    const response = await cartService.removeMany(unique);
    set({ cart: mapServerCart(response.items) });
  },

  clearCart: async () => {
    const response = await cartService.clear();
    set({ cart: mapServerCart(response.items) });
  },

  setCurrentOrder: (order) => set({ currentOrder: order }),

  getTotalAmount: () => {
    const { cart } = get();
    return cart.reduce((total, item) => total + item.effectivePrice * item.quantity, 0);
  },

  getCartItemCount: () => {
    const { cart } = get();
    return cart.reduce((count, item) => count + item.quantity, 0);
  },
}));
